import React, { FC, ReactNode, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '../Button';
import Typography from '../Typography';
import Select, { OptionType } from '../Select';
import translate from '@translate';
import { SxProps } from '@mui/material';

export interface ConfirmDialogStatePropsType {
    title: string;
    action?: (data?: any) => void;
    onCancel?: () => void;
    disabled?: boolean;
    content?: string | ReactNode;
    options?: OptionType[];
    optionsValue?: any;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    customContent?: (data: any, setData: React.Dispatch<any>) => ReactNode;
    okLabel?: string;
    cancelLabel?: string;
    hideCancel?: boolean;
    dataTestId?: string;
}

export interface ConfirmDialogPropsType {
    title: string | ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    action?: (data?: any) => void;
    onCancel?: () => void;
    onClose: () => void;
    disabled?: boolean;
    content?: string | ReactNode;
    options?: OptionType[];
    optionsValue?: any;
    customContent?: (data: any, setData: React.Dispatch<any>) => ReactNode;
    simpleContent?: string | ReactNode;
    hideCancel?: boolean;
    fullWidth?: boolean;
    sx?: SxProps;
    okLabel?: string;
    cancelLabel?: string;
    dataTestId?: string;
}

const ConfirmDialog: FC<ConfirmDialogPropsType> = (props) => {
    const [optionsValue, setOptionsValue] = useState<any>(props.optionsValue || null);
    const [data, setData] = useState<any>({});

    let content: ReactNode | string = '';
    if (props.customContent) {
        content = props.customContent(data, setData);
    } else {
        const renderContent = () => {
            if (typeof props.content === 'string') {
                const lines = props.content.split('\n');
                return lines.map((line, index) => (
                    <React.Fragment key={index}>
                        {line}
                        {index < lines.length - 1 && <br />}
                    </React.Fragment>
                ));
            }
            return props.content;
        };

        content = (
            <>
                <Typography component="div">{renderContent()}</Typography>
                {props.options && (
                    <Select
                        options={props.options}
                        value={optionsValue}
                        onChange={setOptionsValue}
                        fullWidth
                        required
                    />
                )}
            </>
        );
    }

    let disabledOk = false;
    if (props.customContent && data && data.disabled) {
        disabledOk = true;
    }

    const handleOk = () => {
        if (props.action) {
            if (props.customContent) {
                props.action(data);
            } else {
                props.action(optionsValue);
            }
        }
        props.onClose();
    };

    const handleCancel: any = () => {
        if (props.onCancel) {
            props.onCancel();
        }
        props.onClose();
    };

    return (
        <Dialog
            maxWidth={props.maxWidth || 'xs'}
            open
            sx={props.sx}
            fullWidth={props.fullWidth}
            data-testid={props.dataTestId}
        >
            <DialogTitle id="confirmation-dialog-title">{props.title}</DialogTitle>
            <DialogContent dividers>
                {props.simpleContent}
                {content}
            </DialogContent>
            <DialogActions>
                {!props.hideCancel && (
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        data-testid={props.dataTestId ? `${props.dataTestId}-cancel` : undefined}
                    >
                        {props.cancelLabel || translate('confirmDialog.cancel')}
                    </Button>
                )}
                {props.action && (
                    <Button
                        variant="contained"
                        onClick={handleOk}
                        disabled={props.disabled || disabledOk}
                        data-testid={props.dataTestId ? `${props.dataTestId}-ok` : undefined}
                    >
                        {translate(props.okLabel || 'confirmDialog.ok')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
